import { MILESTONE_COLUMNS, ROLE_DEFINITIONS } from './constants';
import { normalizeServiceType, trimText } from './normalization';

export const PRIMARY_MILESTONES = ['Initial Project Delivery', 'Restamp Required'];
export const milestoneKeys = new Set(MILESTONE_COLUMNS.map((column) => column.milestoneType));

export function asNumber(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number : 0;
}

export function optionalNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function splitMemberNames(value) {
  return trimText(value)
    .split('&')
    .map((name) => trimText(name))
    .filter(Boolean);
}

export function memberKey(value) {
  return trimText(value).toLowerCase();
}

export function roleDefinitionByKey(key) {
  return ROLE_DEFINITIONS.find((role) => role.key === key);
}

export function roleFieldValue(row, role) {
  return trimText(row?.[role?.normField] || '');
}

export function rowMatchesMemberForRole(row, role, selectedMember) {
  if (!selectedMember || !role) return false;
  const selected = memberKey(selectedMember);
  return splitMemberNames(roleFieldValue(row, role)).some((name) => memberKey(name) === selected);
}

export function rowMatchesMemberAnyRole(row, selectedMember) {
  return ROLE_DEFINITIONS.some((role) => rowMatchesMemberForRole(row, role, selectedMember));
}

export function milestoneKey(milestoneType) {
  if (milestoneType === 'Initial Project Delivery') return 'initial_project_delivery';
  if (milestoneType === 'Restamp Required') return 'restamp_required';
  if (milestoneType === 'Post Install Letter') return 'post_install_letter';
  return 'other';
}

export function serviceLabel(row) {
  return normalizeServiceType(row?.service_type_norm || row?.service_type) || 'Unassigned';
}

export function makePercentRows(rows, valueKey = 'project_count') {
  const total = rows.reduce((sum, row) => sum + asNumber(row[valueKey]), 0);
  return rows.map((row) => ({
    ...row,
    percent_of_total: total ? Math.round((asNumber(row[valueKey]) * 1000) / total) / 10 : 0,
  }));
}

export function sortByCountThenName(a, b, countKey, nameKey) {
  return asNumber(b[countKey]) - asNumber(a[countKey]) || String(a[nameKey] || '').localeCompare(String(b[nameKey] || ''), undefined, { sensitivity: 'base' });
}

export function countBy(rows, getKey, nameKey) {
  const map = new Map();
  rows.forEach((row) => {
    const key = trimText(getKey(row)) || 'Unassigned';
    map.set(key, (map.get(key) || 0) + 1);
  });
  return makePercentRows(
    [...map.entries()]
      .map(([key, count]) => ({ [nameKey]: key, project_count: count }))
      .sort((a, b) => sortByCountThenName(a, b, 'project_count', nameKey)),
  );
}

export function buildMemberOptions(rows) {
  const map = new Map();
  rows.forEach((row) => {
    ROLE_DEFINITIONS.forEach((role) => {
      splitMemberNames(roleFieldValue(row, role)).forEach((name) => {
        const key = memberKey(name);
        if (key && !map.has(key)) map.set(key, name);
      });
    });
  });
  return [...map.values()].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

export function roleRowsForMember(rows, selectedMember) {
  return ROLE_DEFINITIONS.map((role) => {
    const matchedRows = rows.filter((row) => rowMatchesMemberForRole(row, role, selectedMember));
    const current = {
      role_key: role.key,
      role_label: role.label,
      initial_project_delivery: 0,
      restamp_required: 0,
      post_install_letter: 0,
      other: 0,
      total: 0,
      matchedRows,
    };
    matchedRows.forEach((row) => {
      const key = milestoneKey(row.milestone_type);
      current[key] += 1;
      current.total += 1;
    });
    return current;
  });
}

export function rawValueByHeaders(row, headers = []) {
  const raw = row?.raw_row_data;
  if (!raw || typeof raw !== 'object') return null;

  for (const header of headers) {
    if (raw[header] !== undefined && raw[header] !== null && raw[header] !== '') return raw[header];
  }

  const normalizedTargets = headers.map((header) => String(header || '').replace(/[\s_:\-–—/]+/g, '').toLowerCase());
  const found = Object.entries(raw).find(([key, value]) => {
    if (value === undefined || value === null || value === '') return false;
    const normalizedKey = String(key || '').replace(/[\s_:\-–—/]+/g, '').toLowerCase();
    return normalizedTargets.includes(normalizedKey) || normalizedTargets.some((target) => normalizedKey.endsWith(target));
  });
  return found ? found[1] : null;
}

export function designInProgressTat(row) {
  return optionalNumber(row?.design_in_progress_tat_hours ?? rawValueByHeaders(row, [
    'Design In Progress Turn Around Time Live',
    'Design In Progress TAT Live',
    'Design In Progress Turnaround Time Live',
    'AZ',
  ]));
}

export function designReviewInProgressTat(row) {
  return optionalNumber(row?.design_review_in_progress_tat_hours ?? rawValueByHeaders(row, [
    'Design Review In Progress TAT Live',
    'Design Review In Progress TAT',
    'AY',
  ]));
}

export function readyForPeTat(row) {
  return optionalNumber(row?.ready_for_pe_tat_hours ?? rawValueByHeaders(row, [
    'Ready for PE Turn Around Time Live',
    'Ready for PE TAT Hours',
    'Ready for PE TAT',
    'N',
  ]));
}

export function eeTurnaroundTat(row) {
  return optionalNumber(row?.ee_tat_hours ?? rawValueByHeaders(row, [
    'EE Turn Around Time Live',
    'EE TAT Hours',
    'EE TAT',
    'Q',
  ]));
}

export function peInProgressTat(row) {
  return optionalNumber(row?.pe_in_progress_tat_hours ?? rawValueByHeaders(row, [
    'PE In Progress Turn Around Time Live',
    'PE In Progress TAT Live',
    'PE In Progress Turnaround Time Live',
    'BA',
  ]));
}

export function eeInProgressTat(row) {
  return optionalNumber(row?.ee_in_progress_tat_hours ?? rawValueByHeaders(row, [
    'EE In Progress Turn Around Time Live',
    'EE In Progress TAT Live',
    'EE In Progress Turnaround Time Live',
    'BB',
  ]));
}

export function qaResult(row) {
  return trimText(row?.milestone_qa_result_norm || row?.milestone_qa_result || rawValueByHeaders(row, [
    'Milestone QA Result',
    'AU',
  ]));
}

export function isNoError(row) {
  return qaResult(row).replace(/[\s_-]+/g, '').toLowerCase() === 'noerror';
}

export function isBlankQa(row) {
  return !qaResult(row);
}

export function creRestampReason(row) {
  return trimText(row?.cre_restamp_reason || rawValueByHeaders(row, [
    'Milestone CRE Restamp Reason',
    'CRE Restamp Reason',
    'AM',
  ]));
}

export function normalizeCreReasonToken(value) {
  return trimText(value)
    .toLowerCase()
    .replace(/[–—]/g, '-')
    .replace(/\s+/g, ' ')
    .replace(/\s*-\s*/g, ' - ')
    .trim();
}

export function compactCreReasonToken(value) {
  return normalizeCreReasonToken(value).replace(/[\s_:/\\\-]+/g, '');
}

export function parseCreRestampReasons(rowOrValue) {
  const value = typeof rowOrValue === 'string' ? rowOrValue : creRestampReason(rowOrValue);
  if (!value) return [];

  return String(value)
    .replace(/[–—]/g, '-')
    .split(/[,;|\n]+/)
    .map(normalizeCreReasonToken)
    .filter(Boolean);
}

export function isCreErrorForScope(row, scope) {
  const target = String(scope || '').toLowerCase();
  if (!target) return false;

  const canonical = `creerrors${target}`;

  return parseCreRestampReasons(row).some((reason) => {
    const compact = compactCreReasonToken(reason);

    // Exact role bucket check after splitting multi-select values.
    // Example: "CRE Errors - PE, CRE Errors - EE" should fail PE and EE, but not Design.
    if (compact === canonical) return true;

    // Fallback for small Salesforce/export naming differences like plural/singular error.
    return compact.includes('cre')
      && (compact.includes('error') || compact.includes('errors'))
      && compact.endsWith(target);
  });
}

export const ROLE_PERFORMANCE_DEFINITIONS = [
  {
    key: 'design',
    label: 'Design',
    sectionTitle: 'Design Performance',
    roleKey: 'design_engineer',
    outputRule: 'Count as 1 if the team member is the Milestone Design Engineer.',
    qualityRule: 'No Error percentage uses Milestone QA Result. Blank QA rows are excluded from the percentage.',
    tatLabel: 'Design In Progress TAT',
    initialThresholdHours: 2.5,
    restampThresholdHours: 0.75,
    qualityMode: 'qa',
    tatValue: designInProgressTat,
  },
  {
    key: 'design_review',
    label: 'Design Review',
    sectionTitle: 'Design Review Performance',
    roleKey: 'design_reviewer',
    outputRule: 'Count as 1 if the team member is the Milestone Design Reviewer.',
    qualityRule: 'Initial quality is N/A. Restamp quality counts errors when CRE Restamp Reason is CRE Errors - Design.',
    tatLabel: 'Design Review In Progress TAT',
    initialThresholdHours: 1,
    restampThresholdHours: 0.5,
    qualityMode: 'cre_restamp',
    creScope: 'design',
    tatValue: designReviewInProgressTat,
  },
  {
    key: 'pe',
    label: 'PE',
    sectionTitle: 'PE Performance',
    roleKey: 'pe_engineer',
    outputRule: 'Count as 1 if the team member is the Milestone Structural Engineer.',
    qualityRule: 'Initial quality is N/A. Restamp quality counts errors when CRE Restamp Reason is CRE Errors - PE.',
    tatLabel: 'PE In Progress TAT',
    initialThresholdHours: 0.5,
    restampThresholdHours: 20 / 60,
    qualityMode: 'cre_restamp',
    creScope: 'pe',
    tatValue: peInProgressTat,
  },
  {
    key: 'ee',
    label: 'EE',
    sectionTitle: 'EE Performance',
    roleKey: 'ee_engineer',
    outputRule: 'Count as 1 if the team member is the Milestone Electrical Engineer.',
    qualityRule: 'Initial quality is N/A. Restamp quality counts errors when CRE Restamp Reason is CRE Errors - EE.',
    tatLabel: 'EE In Progress TAT',
    initialThresholdHours: 0.5,
    restampThresholdHours: 20 / 60,
    qualityMode: 'cre_restamp',
    creScope: 'ee',
    tatValue: eeInProgressTat,
  },
].map((definition) => ({
  ...definition,
  role: roleDefinitionByKey(definition.roleKey),
}));

export function thresholdForMilestone(definition, milestoneType) {
  if (milestoneType === 'Initial Project Delivery') return definition.initialThresholdHours;
  if (milestoneType === 'Restamp Required') return definition.restampThresholdHours;
  return null;
}

export function formatThreshold(value) {
  if (value === null || value === undefined) return 'N/A';
  if (value < 1) return `${Math.round(value * 60)} min`;
  if (value === 1) return '1 hr';
  return `${Number(value).toFixed(value % 1 ? 2 : 0)} hrs`;
}

export function buildRoleMetricRows(roleRows, definition) {
  return PRIMARY_MILESTONES.map((milestoneType) => {
    const scopedRows = roleRows.filter((row) => row.milestone_type === milestoneType);
    const output = scopedRows.length;
    const threshold = thresholdForMilestone(definition, milestoneType);
    const tatOnTime = threshold === null ? 0 : scopedRows.filter((row) => {
      const value = optionalNumber(definition.tatValue(row));
      return value !== null && value > 0 && value <= threshold;
    }).length;
    const tatLate = threshold === null ? 0 : output - tatOnTime;

    let qualityApplicable = false;
    let qualityPassed = null;
    let qualityFailed = null;
    let qualityBlank = null;
    let qualityEligible = null;
    let qualityPercent = null;

    if (definition.qualityMode === 'qa') {
      const blankRows = scopedRows.filter(isBlankQa).length;
      const eligibleRows = scopedRows.filter((row) => !isBlankQa(row));
      const noErrorRows = eligibleRows.filter(isNoError).length;
      qualityApplicable = true;
      qualityPassed = noErrorRows;
      qualityFailed = Math.max(0, eligibleRows.length - noErrorRows);
      qualityBlank = blankRows;
      qualityEligible = eligibleRows.length;
      qualityPercent = eligibleRows.length ? Math.round((noErrorRows * 1000) / eligibleRows.length) / 10 : null;
    } else if (definition.qualityMode === 'cre_restamp' && milestoneType === 'Restamp Required') {
      const errors = scopedRows.filter((row) => isCreErrorForScope(row, definition.creScope)).length;
      qualityApplicable = true;
      qualityFailed = errors;
      qualityPassed = Math.max(0, output - errors);
      qualityBlank = 0;
      qualityEligible = output;
      qualityPercent = output ? Math.round(((output - errors) * 1000) / output) / 10 : null;
    }

    return {
      milestone_type: milestoneType,
      output_count: output,
      quality_applicable: qualityApplicable,
      quality_passed: qualityPassed,
      quality_failed: qualityFailed,
      quality_blank: qualityBlank,
      quality_eligible: qualityEligible,
      quality_percent: qualityPercent,
      tat_threshold_hours: threshold,
      tat_threshold_label: formatThreshold(threshold),
      tat_on_time: tatOnTime,
      tat_late: tatLate,
      tat_on_time_percent: output ? Math.round((tatOnTime * 1000) / output) / 10 : 0,
    };
  }).filter((row) => row.output_count > 0);
}

export function buildTatStatusRowsFromMetric(metricRow) {
  if (!metricRow || asNumber(metricRow.output_count) <= 0) return [];
  return makePercentRows([
    { status: 'On-Time', project_count: asNumber(metricRow.tat_on_time) },
    { status: 'Late', project_count: asNumber(metricRow.tat_late) },
  ]).filter((row) => asNumber(row.project_count) > 0);
}

export function buildQualityStatusRowsFromMetric(metricRow, definition) {
  if (!metricRow?.quality_applicable) return [];
  if (definition.qualityMode === 'qa') {
    return makePercentRows([
      { status: 'No Error', project_count: asNumber(metricRow.quality_passed) },
      { status: 'Error / Other', project_count: asNumber(metricRow.quality_failed) },
      { status: 'Blank', project_count: asNumber(metricRow.quality_blank) },
    ]).filter((row) => asNumber(row.project_count) > 0);
  }
  return makePercentRows([
    { status: 'No CRE Error', project_count: asNumber(metricRow.quality_passed) },
    { status: `CRE Errors - ${String(definition.creScope || '').toUpperCase()}`, project_count: asNumber(metricRow.quality_failed) },
  ]).filter((row) => asNumber(row.project_count) > 0);
}

export function buildRolePerformanceSections(rows, selectedMember) {
  return ROLE_PERFORMANCE_DEFINITIONS.map((definition) => {
    const roleRows = rows.filter((row) => rowMatchesMemberForRole(row, definition.role, selectedMember));
    const metricRows = buildRoleMetricRows(roleRows, definition);
    return {
      definition,
      roleRows,
      metricRows,
      serviceRows: countBy(roleRows, serviceLabel, 'service_type'),
      totalOutput: roleRows.length,
    };
  }).filter((section) => section.totalOutput > 0);
}



export function assignmentRowsForMember(rows, selectedMember, limit = 300) {
  const output = [];
  (rows || []).forEach((row) => {
    ROLE_PERFORMANCE_DEFINITIONS.forEach((definition) => {
      if (!rowMatchesMemberForRole(row, definition.role, selectedMember)) return;
      const threshold = thresholdForMilestone(definition, row.milestone_type);
      const tatValue = optionalNumber(definition.tatValue(row));
      const isPrimaryMilestone = PRIMARY_MILESTONES.includes(row.milestone_type);
      const status = isPrimaryMilestone && threshold !== null && threshold !== undefined
        ? (tatValue !== null && tatValue > 0 && tatValue <= threshold ? 'On-Time' : 'Late')
        : 'N/A';

      output.push({
        project_name: row.project_name,
        milestone_date: row.milestone_date,
        milestone_type: row.milestone_type || 'Other',
        assigned_role: definition.label,
        service_type: serviceLabel(row),
        tat_hours: tatValue,
        target_tat_hours: threshold,
        target_tat_label: formatThreshold(threshold),
        qa_result: qaResult(row) || 'Not Set',
        cre_restamp_reason: creRestampReason(row) || 'Not Set',
        status,
        source_row_number: row.source_row_number,
      });
    });
  });
  return output
    .sort((a, b) => asNumber(a.source_row_number) - asNumber(b.source_row_number) || String(a.assigned_role || '').localeCompare(String(b.assigned_role || '')))
    .slice(0, limit);
}

export function roundOne(value) {
  const number = Number(value || 0);
  return Math.round(number * 10) / 10;
}

export function buildIndividualKpiSummary(rolePerformanceSections) {
  const sections = rolePerformanceSections || [];
  const assignmentRows = [];
  let weightedTatHours = 0;
  let outputCount = 0;
  const workedDays = new Set();

  sections.forEach((section) => {
    const definition = section.definition;
    (section.roleRows || []).forEach((row) => {
      if (!PRIMARY_MILESTONES.includes(row.milestone_type)) return;
      const threshold = thresholdForMilestone(definition, row.milestone_type);
      if (threshold === null || threshold === undefined) return;
      assignmentRows.push(row);
      outputCount += 1;

      // Output Quantity uses weighted target TAT hours per completed task.
      // Example for Design: (initial output × 2.5 hrs) + (restamp output × 0.75 hr), then divide by worked days.
      weightedTatHours += threshold;

      const dateKey = trimText(row?.milestone_date || '').slice(0, 10);
      if (dateKey) workedDays.add(dateKey);
    });
  });

  const workedDayCount = workedDays.size;
  const outputPerDay = workedDayCount && outputCount ? roundOne(weightedTatHours / workedDayCount) : null;

  let qualityPassed = 0;
  let qualityEligible = 0;
  let tatOnTime = 0;
  let tatTotal = 0;

  sections.forEach((section) => {
    (section.metricRows || []).forEach((row) => {
      if (row.quality_applicable && asNumber(row.quality_eligible) > 0) {
        qualityPassed += asNumber(row.quality_passed);
        qualityEligible += asNumber(row.quality_eligible);
      }
      if (asNumber(row.output_count) > 0) {
        tatOnTime += asNumber(row.tat_on_time);
        tatTotal += asNumber(row.tat_on_time) + asNumber(row.tat_late);
      }
    });
  });

  const qualityPercent = qualityEligible ? roundOne((qualityPassed * 100) / qualityEligible) : null;
  const tatPercent = tatTotal ? roundOne((tatOnTime * 100) / tatTotal) : null;

  return [
    {
      key: 'output_quantity',
      label: 'Output Quantity',
      value: outputPerDay === null ? 'N/A' : outputPerDay,
      format: outputPerDay === null ? 'text' : 'hoursPerDay',
      helper: `${outputCount} output${outputCount === 1 ? '' : 's'} · ${roundOne(weightedTatHours)} weighted target hrs ÷ ${workedDayCount || 0} worked day${workedDayCount === 1 ? '' : 's'}`,
      exportValue: outputPerDay,
    },
    {
      key: 'quality',
      label: 'Quality',
      value: qualityPercent === null ? 'N/A' : qualityPercent,
      format: qualityPercent === null ? 'text' : 'percent',
      helper: qualityEligible ? `${qualityPassed} passed / ${qualityEligible} graded task${qualityEligible === 1 ? '' : 's'}` : 'No graded tasks',
      exportValue: qualityPercent,
    },
    {
      key: 'turnaround_time',
      label: 'Turnaround Time',
      value: tatPercent === null ? 'N/A' : tatPercent,
      format: tatPercent === null ? 'text' : 'percent',
      helper: tatTotal ? `${tatOnTime} on-time / ${tatTotal} task${tatTotal === 1 ? '' : 's'}` : 'No TAT tasks',
      exportValue: tatPercent,
    },
  ];
}

export function uniqueProjectCount(rows) {
  return new Set(rows.map((row) => row.project_key || row.project_name || row.id).filter(Boolean)).size;
}

export function rowsHavePositiveValue(rows, keys = ['project_count']) {
  return (rows || []).some((row) => keys.some((key) => asNumber(row?.[key]) > 0));
}
